/**
 * The Company Lists name filter (`q`) and page, held either in component state
 * or in the URL — the Company Lists cut of People Lists'
 * `useImportsListFilters` (`routes/research/imports/`), with the same param
 * names (`q`, `page`) and the same rules:
 *
 * - URL mode is OPT-IN (`persist`). The firm page uses it, so opening a list
 *   and coming back (the row carries `pathname + search` in `state.from`) or a
 *   refresh lands on the same filter and page. A search's Company Lists tab
 *   does not, exactly as People Lists' Search Detail tab does not.
 * - Every write is `{ replace: true }`: a filter is a view of the page, not a
 *   place Back should step through.
 * - A filter change resets the page IN THE SAME WRITE (it drops `page`), so
 *   no render ever pairs the new filter with the old page.
 * - `page` is read leniently, as People Lists reads it: garbage or < 1 is 1.
 *   Absent means 1, and page 1 is written as no param.
 *
 * KEEP IN STEP with `routes/research/imports/useImportsListFilters.ts`: the
 * two must change together on `readPage` (lenient parse, garbage → 1), the
 * `{ replace: true }` writes, and "a filter write drops `page`". Two lists
 * that read `?page=` differently is how a shared link lands on different
 * pages.
 *
 * The "Imported by" scope (`owner`: `you` | `all` | an email) is ported too
 * (Trello #831), with People Lists' rules: it starts at `initialOwner`, which
 * is also what an absent `?owner=` reads as, and a scope change drops `page`
 * in the same write.
 *
 * The sort lives here too (`sort` / `dir`, Trello #831 review M2): in the URL
 * on the firm page, so a refresh with `?page=N` reads the same rows in the
 * same order; in state on a search's tab. The default (Date, newest first) is
 * no params, and a sort change drops `page` in the same write. (People Lists
 * keeps its sort in state; this is a deliberate step past it.)
 *
 * Not ported: tag chips, which Company Lists does not have. The search filter
 * (`search_id_thrive`) stays the page's own concern, as on `ImportsPage`,
 * including widening `you` to `all` when one arrives.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { CompanyListSortKey } from "../../clients/companyLists.client";
import type { ListSort } from "./queries";

const Q_PARAM = "q";
export const PAGE_PARAM = "page";
export const OWNER_PARAM = "owner";
const SORT_PARAM = "sort";
const DIR_PARAM = "dir";
const LIST_SORT_KEYS: readonly CompanyListSortKey[] = [
  "created_at",
  "name",
  "company_count",
  "created_by_email",
];
/** Date, newest first: People Lists' default. */
export const DEFAULT_LIST_SORT: ListSort = { key: "created_at", dir: "desc" };

function readSort(params: URLSearchParams): ListSort {
  const key = params.get(SORT_PARAM) as CompanyListSortKey | null;
  if (!key || !LIST_SORT_KEYS.includes(key)) return DEFAULT_LIST_SORT;
  return { key, dir: params.get(DIR_PARAM) === "asc" ? "asc" : "desc" };
}

/** People Lists' `handleSort`: a new column starts ascending, the same flips. */
function nextSort(current: ListSort, key: CompanyListSortKey): ListSort {
  return current.key === key
    ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
    : { key, dir: "asc" };
}

export interface CompanyListsFilters {
  /** What the box shows (raw, un-debounced). */
  searchText: string;
  /** The committed, trimmed name filter the query uses; "" = none. */
  q: string;
  page: number;
  /** "Imported by": `you` | `all` | an email. */
  owner: string;
  setSearchText: (next: string) => void;
  /** Commit the (debounced) box and go back to page 1, in one update. */
  commitQ: (next: string) => void;
  /** Move the page without touching the filter. */
  goToPage: (next: number) => void;
  /** Change the scope and go back to page 1, in one update. */
  setOwner: (next: string) => void;
  sort: ListSort;
  /** Sort by a column (or flip it) and go back to page 1, in one update. */
  toggleSort: (key: CompanyListSortKey) => void;
}

/** Same parse as People Lists' `readPage`. */
function readPage(params: URLSearchParams): number {
  const parsed = Number.parseInt(params.get(PAGE_PARAM) ?? "1", 10);
  if (!Number.isSafeInteger(parsed)) return 1;
  return Math.max(1, parsed);
}

export function useCompanyListsFilters({
  persist,
  initialOwner = "all",
}: {
  persist: boolean;
  /** The scope to start at; in URL mode, what an absent `?owner=` means. */
  initialOwner?: string;
}): CompanyListsFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  const [localOwner, setLocalOwner] = useState(initialOwner);
  const [localQ, setLocalQ] = useState("");
  const [localPage, setLocalPage] = useState(1);
  const [localSort, setLocalSort] = useState<ListSort>(DEFAULT_LIST_SORT);
  // Seeded from the URL once, so the box shows the filter the reader came
  // back to; after that it is the reader's draft.
  const [searchText, setSearchText] = useState(() =>
    persist ? (searchParams.get(Q_PARAM)?.trim() ?? "") : ""
  );

  const q = persist ? (searchParams.get(Q_PARAM)?.trim() ?? "") : localQ;
  const page = persist ? readPage(searchParams) : localPage;
  const owner = persist ? searchParams.get(OWNER_PARAM)?.trim() || initialOwner : localOwner;
  const urlSortKey = readSort(searchParams).key;
  const urlSortDir = readSort(searchParams).dir;
  const urlSort = useMemo<ListSort>(
    () => ({ key: urlSortKey, dir: urlSortDir }),
    [urlSortKey, urlSortDir]
  );
  const sort = persist ? urlSort : localSort;

  // Stable callbacks that still see the current setter (as in People Lists).
  const latest = useRef({ setSearchParams, persist });
  useLayoutEffect(() => {
    latest.current = { setSearchParams, persist };
  });

  const commitQ = useCallback((next: string) => {
    const trimmed = next.trim();
    if (!latest.current.persist) {
      setLocalQ(trimmed);
      setLocalPage(1);
      return;
    }
    latest.current.setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (trimmed) params.set(Q_PARAM, trimmed);
        else params.delete(Q_PARAM);
        params.delete(PAGE_PARAM);
        return params;
      },
      { replace: true }
    );
  }, []);

  const goToPage = useCallback((next: number) => {
    const target = Math.max(1, next);
    if (!latest.current.persist) {
      setLocalPage(target);
      return;
    }
    latest.current.setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (target <= 1) params.delete(PAGE_PARAM);
        else params.set(PAGE_PARAM, String(target));
        return params;
      },
      { replace: true }
    );
  }, []);

  const setOwner = useCallback((next: string) => {
    if (!latest.current.persist) {
      setLocalOwner(next);
      setLocalPage(1);
      return;
    }
    latest.current.setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set(OWNER_PARAM, next);
        params.delete(PAGE_PARAM);
        return params;
      },
      { replace: true }
    );
  }, []);

  const toggleSort = useCallback((key: CompanyListSortKey) => {
    if (!latest.current.persist) {
      setLocalSort((prev) => nextSort(prev, key));
      setLocalPage(1);
      return;
    }
    latest.current.setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        const next = nextSort(readSort(params), key);
        if (next.key === DEFAULT_LIST_SORT.key && next.dir === DEFAULT_LIST_SORT.dir) {
          params.delete(SORT_PARAM);
          params.delete(DIR_PARAM);
        } else {
          params.set(SORT_PARAM, next.key);
          params.set(DIR_PARAM, next.dir);
        }
        params.delete(PAGE_PARAM);
        return params;
      },
      { replace: true }
    );
  }, []);

  return {
    searchText,
    q,
    page,
    owner,
    sort,
    setSearchText,
    commitQ,
    goToPage,
    setOwner,
    toggleSort,
  };
}
